<template>
  <div>
    <Head :title="`Detalle de Caja - ${getUserName(caja.user)}`" />

    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
      <div class="flex items-center gap-4">
        <button 
          class="p-3 text-white bg-teal-700 hover:bg-teal-800 rounded-lg transition-colors"
          title="Volver atrás"
          @click="back()"
        >
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <div class="flex items-center gap-3">
            <h1 class="text-2xl sm:text-3xl font-bold">Caja: {{ getUserName(caja.user) }}</h1>
            <span 
              :class="[
                'px-2.5 py-1 text-xs font-bold rounded-full uppercase tracking-wider',
                caja.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              ]"
            >
              {{ caja.status === 'open' ? 'Abierta' : 'Cerrada' }}
            </span>
          </div>
          <p class="text-gray-500 text-sm mt-1 flex items-center gap-2">
            <span>Abierta el {{ formatDate(caja.opened_at) }} por {{ getUserName(caja.opened_by) }}</span>
            <span v-if="caja.deposit" class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
              <i class="fa fa-store"></i>
              <span>{{ caja.deposit.name }}</span>
            </span>
          </p>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex gap-3 w-full sm:w-auto">
        <a 
          :href="`/cajas/${caja.id}/report`" 
          target="_blank"
          class="flex-1 sm:flex-none px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm text-sm"
        >
          <i class="fa fa-file-pdf"></i>
          <span>Reporte PDF</span>
        </a>
      </div>
    </div>

    <!-- KPIs Cards Grid -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
      <!-- Apertura -->
      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Apertura</div>
        <div class="text-lg sm:text-xl font-bold text-gray-900">${{ formatPrice(caja.opening_amount) }}</div>
        <div class="text-xs font-semibold text-gray-700 mt-1">Bs {{ formatPrice(caja.opening_amount_bs) }}</div>
        <p class="text-[11px] text-gray-400 mt-1">Efectivo inicial</p>
      </div>

      <!-- Ventas -->
      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Ventas del Día</div>
        <div class="text-lg sm:text-xl font-bold text-blue-900">${{ formatPrice(stats.total_sales) }}</div>
        <p class="text-[11px] text-blue-600 mt-1">{{ orders.length }} órdenes</p>
      </div>

      <!-- Recaudado -->
      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Recaudado</div>
        <div class="text-lg sm:text-xl font-bold text-green-900">${{ formatPrice(stats.total_payments) }}</div>
        <div class="text-[11px] text-gray-500 mt-1 flex flex-col">
          <span>Efec: ${{ formatPrice(stats.total_cash_received) }}</span>
          <span>Otros: ${{ formatPrice(stats.total_non_cash_payments) }}</span>
        </div>
      </div>

      <!-- Ingresos Manuales -->
      <div class="bg-white border border-green-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2 flex items-center justify-between">
          <span>Ingresos (+)</span>
          <i class="fa fa-arrow-down text-green-600 text-xs"></i>
        </div>
        <div class="text-lg sm:text-xl font-bold text-green-700">+${{ formatPrice(stats.total_incomes) }}</div>
        <div class="text-xs font-semibold text-green-600 mt-1">+Bs {{ formatPrice(stats.total_incomes_bs) }}</div>
        <p class="text-[11px] text-green-600/70 mt-1">Entradas manuales</p>
      </div>

      <!-- Vueltos -->
      <div class="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Vueltos Entregados</div>
        <div class="text-lg sm:text-xl font-bold text-amber-900">${{ formatPrice(stats.total_change_given) }}</div>
        <div class="text-[11px] text-gray-500 mt-1 flex flex-col">
          <span v-for="c in change_by_method" :key="c.method" class="truncate" :title="`${c.method}: $${formatPrice(c.total_usd)}`">
            {{ c.method }}: ${{ formatPrice(c.total_usd) }}
          </span>
          <span v-if="change_by_method.length === 0" class="text-gray-400">Sin vueltos</span>
        </div>
      </div>

      <!-- Retiros -->
      <div class="bg-white border border-red-100 rounded-xl p-4 shadow-sm">
        <div class="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2 flex items-center justify-between">
          <span>Retiros (-)</span>
          <i class="fa fa-arrow-up text-red-500 text-xs"></i>
        </div>
        <div class="text-lg sm:text-xl font-bold text-red-700">-${{ formatPrice(stats.total_withdrawals) }}</div>
        <div class="text-xs font-semibold text-red-600 mt-1">-Bs {{ formatPrice(stats.total_withdrawals_bs) }}</div>
        <p class="text-[11px] text-red-600/70 mt-1">Salidas manuales</p>
      </div>

      <!-- Balance Esperado -->
      <div class="bg-white border border-teal-200 rounded-xl p-4 shadow-sm col-span-2 sm:col-span-1 bg-gradient-to-br from-white to-teal-50/40">
        <div class="text-xs font-semibold text-teal-800 uppercase tracking-wider mb-2">Efectivo Esperado</div>
        <div class="text-lg sm:text-xl font-bold text-teal-900">${{ formatPrice(stats.expected_cash) }}</div>
        <div class="text-xs font-semibold text-teal-800 mt-1">Bs {{ formatPrice(stats.expected_cash_bs) }}</div>
        <p class="text-[11px] text-teal-600 mt-1 font-medium">Balance en caja</p>
      </div>
    </div>

    <!-- Closed Box Details -->
    <div 
      v-if="caja.status === 'closed'" 
      class="mb-8 p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6"
    >
      <div>
        <h4 class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Cierre Real Reportado</h4>
        <div class="text-2xl font-bold text-gray-900">${{ formatPrice(caja.closing_amount) }}</div>
        <div class="text-lg font-bold text-gray-700">Bs {{ formatPrice(caja.closing_amount_bs) }}</div>
        <p class="text-xs text-gray-500 mt-1">Cerrado por: {{ getUserName(caja.closed_by) }}</p>
      </div>

      <div>
        <h4 class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Discrepancia / Diferencia</h4>
        <div 
          :class="[
            'text-2xl font-bold',
            discrepancy === 0 ? 'text-green-600' : (discrepancy > 0 ? 'text-blue-600' : 'text-red-600')
          ]"
        >
          USD: {{ discrepancy >= 0 ? '+' : '' }}${{ formatPrice(discrepancy) }}
        </div>
        <div 
          :class="[
            'text-lg font-bold mt-1',
            discrepancy_bs === 0 ? 'text-green-600' : (discrepancy_bs > 0 ? 'text-blue-600' : 'text-red-600')
          ]"
        >
          Bs: {{ discrepancy_bs >= 0 ? '+' : '' }}{{ formatPrice(discrepancy_bs) }}
        </div>
        <p class="text-xs text-gray-500 mt-1">
          <span v-if="discrepancy === 0 && discrepancy_bs === 0">Caja Cuadrada Perfecta</span>
          <span v-else-if="discrepancy > 0 || discrepancy_bs > 0">Sobrante de efectivo</span>
          <span v-else>Faltante de efectivo</span>
        </p>
      </div>

      <div>
        <h4 class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Fecha de Cierre</h4>
        <div class="text-lg font-semibold text-gray-800">{{ formatDate(caja.closed_at) }}</div>
        <p class="text-xs text-gray-500 mt-1" v-if="caja.notes"><strong>Notas:</strong> {{ caja.notes }}</p>
      </div>
    </div>

    <!-- Active Drawer Actions (Withdrawals / Closing) -->
    <div 
      v-if="caja.status === 'open'"
      class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8"
    >
      <!-- Register Movement (Income / Withdrawal) -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div class="flex items-center justify-between pb-3 mb-4 border-b border-gray-100">
          <h3 class="text-lg font-bold text-gray-800 flex items-center gap-2">
            <i 
              :class="[
                'fa',
                movementForm.type === 'income' ? 'fa-plus-circle text-green-600' : 'fa-minus-circle text-red-600'
              ]"
            ></i>
            <span>{{ movementForm.type === 'income' ? 'Registrar Ingreso de Caja' : 'Registrar Retiro de Caja' }}</span>
          </h3>
          <span 
            :class="[
              'px-2.5 py-0.5 text-xs font-bold rounded-full uppercase tracking-wider',
              movementForm.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            ]"
          >
            {{ movementForm.type === 'income' ? 'Entrada (+)' : 'Salida (-)' }}
          </span>
        </div>

        <!-- Toggle Type -->
        <div class="grid grid-cols-2 gap-2 p-1.5 bg-gray-100 rounded-xl mb-5 border border-gray-200">
          <button 
            type="button"
            @click="movementForm.type = 'income'"
            :class="[
              'py-2.5 px-4 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer',
              movementForm.type === 'income' 
                ? 'bg-green-600 text-white shadow' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/70'
            ]"
          >
            <i class="fa fa-plus-circle text-sm"></i>
            <span>Ingreso de Dinero (+)</span>
          </button>
          <button 
            type="button"
            @click="movementForm.type = 'withdrawal'"
            :class="[
              'py-2.5 px-4 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer',
              movementForm.type === 'withdrawal' 
                ? 'bg-red-600 text-white shadow' 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/70'
            ]"
          >
            <i class="fa fa-minus-circle text-sm"></i>
            <span>Egreso / Retiro (-)</span>
          </button>
        </div>
        
        <form @submit.prevent="submitMovement" class="space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Monto (USD)</label>
              <input 
                v-model="movementForm.amount"
                type="number"
                step="0.01"
                min="0"
                class="w-full form-input rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
                placeholder="0.00"
              />
              <p v-if="movementForm.errors.amount" class="text-red-500 text-xs mt-1">{{ movementForm.errors.amount }}</p>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Monto (Bs)</label>
              <input 
                v-model="movementForm.amount_bs"
                type="number"
                step="0.01"
                min="0"
                class="w-full form-input rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
                placeholder="0.00"
              />
              <p v-if="movementForm.errors.amount_bs" class="text-red-500 text-xs mt-1">{{ movementForm.errors.amount_bs }}</p>
            </div>
          </div>
          <p class="text-xs text-gray-400">Puede ingresar monto en USD, en Bs o en ambos simultáneamente.</p>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-1">Concepto / Motivo</label>
            <input 
              v-model="movementForm.description"
              type="text"
              required
              class="w-full form-input rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
              :placeholder="movementForm.type === 'income' ? 'Ej. Inyección de sencillo, cambio de efectivo, etc.' : 'Ej. Pago de flete, compras menores, etc.'"
            />
            <p v-if="movementForm.errors.description" class="text-red-500 text-xs mt-1">{{ movementForm.errors.description }}</p>
          </div>

          <div class="flex justify-end pt-2">
            <button 
              type="submit"
              :disabled="movementForm.processing"
              :class="[
                'px-5 py-2.5 text-white font-bold rounded-lg transition-colors text-sm flex items-center gap-2 shadow-sm cursor-pointer',
                movementForm.type === 'income' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              ]"
            >
              <i v-if="movementForm.processing" class="fa fa-spinner fa-spin"></i>
              <i v-else :class="['fa', movementForm.type === 'income' ? 'fa-plus-circle' : 'fa-minus-circle']"></i>
              <span>{{ movementForm.type === 'income' ? 'Procesar Ingreso' : 'Procesar Retiro' }}</span>
            </button>
          </div>
        </form>
      </div>

      <!-- Close Register -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 class="text-lg font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
          <i class="fa fa-lock text-teal-800"></i>
          <span>Cerrar Caja Registradora</span>
        </h3>
        
        <form @submit.prevent="submitCloseCaja" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <text-input 
                v-model="closeForm.closing_amount"
                type="number"
                step="0.01"
                label="Efectivo en Caja (USD)"
                placeholder="Monto en USD"
                required
                :error="closeForm.errors.closing_amount"
              />
            </div>
            <div>
              <text-input 
                v-model="closeForm.closing_amount_bs"
                type="number"
                step="0.01"
                label="Efectivo en Caja (Bs)"
                placeholder="Monto en Bs"
                required
                :error="closeForm.errors.closing_amount_bs"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-gray-700 mb-2">Comentarios de Cierre</label>
            <textarea 
              v-model="closeForm.notes"
              rows="2"
              class="w-full form-textarea rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
              placeholder="Notas sobre el arqueo de caja, diferencias, etc. (opcional)..."
            ></textarea>
            <p v-if="closeForm.errors.notes" class="text-red-500 text-xs mt-1">{{ closeForm.errors.notes }}</p>
          </div>

          <div class="flex justify-end pt-2">
            <button 
              type="submit"
              :disabled="closeForm.processing"
              class="px-4 py-2 bg-teal-800 hover:bg-teal-900 text-white font-bold rounded-lg transition-colors text-sm flex items-center gap-2 shadow-sm"
            >
              <i v-if="closeForm.processing" class="fa fa-spinner fa-spin"></i>
              <span>Cerrar Caja (Arqueo)</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Detailed lists -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <!-- Tabs Navigation -->
      <div class="flex border-b border-gray-100">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            'flex-1 sm:flex-none px-6 py-4 font-bold text-sm border-b-2 transition-colors',
            activeTab === tab.id 
              ? 'border-teal-700 text-teal-800 bg-teal-50/20' 
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50/50'
          ]"
        >
          {{ tab.name }} ({{ tab.count }})
        </button>
      </div>

      <!-- Tab: Ventas -->
      <div v-if="activeTab === 'orders'" class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm">
          <thead>
            <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase">
              <th class="py-4 px-6">N° Venta</th>
              <th class="py-4 px-6">Cliente</th>
              <th class="py-4 px-6">Fecha/Hora</th>
              <th class="py-4 px-6">Vendedor</th>
              <th class="py-4 px-6 text-right">Total</th>
              <th class="py-4 px-6 text-right">Deuda</th>
              <th class="py-4 px-6 text-center">Estado</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="order in orders" :key="order.id" class="hover:bg-gray-50 transition-colors">
              <td class="py-4 px-6 font-semibold text-teal-800">
                <Link :href="`/orders/${order.id}`">#{{ order.number_order ?? order.id }}</Link>
              </td>
              <td class="py-4 px-6">
                <div>{{ order.client.name }}</div>
                <div class="text-xs text-gray-400">{{ order.client.identification }}</div>
              </td>
              <td class="py-4 px-6 text-gray-600">{{ formatDate(order.created_at) }}</td>
              <td class="py-4 px-6 text-gray-600">{{ order.responsible ? getUserName(order.responsible) : 'Venta directa' }}</td>
              <td class="py-4 px-6 text-right font-medium text-gray-900">${{ formatPrice(order.total) }}</td>
              <td class="py-4 px-6 text-right font-medium text-red-600">${{ formatPrice(order.total - order.balance) }}</td>
              <td class="py-4 px-6 text-center">
                <span 
                  :class="[
                    'px-2 py-0.5 text-xs font-bold rounded-full',
                    getOrderStateClass(order.order_state)
                  ]"
                >
                  {{ getOrderStateText(order.order_state) }}
                </span>
              </td>
            </tr>
            <tr v-if="orders.length === 0">
              <td colspan="7" class="text-center py-8 text-gray-500">
                No se registraron ventas en esta caja.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tab: Pagos Recibidos -->
      <div v-if="activeTab === 'payments'" class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm">
          <thead>
            <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase">
              <th class="py-4 px-6">Fecha/Hora</th>
              <th class="py-4 px-6">N° Orden</th>
              <th class="py-4 px-6">Método de Pago</th>
              <th class="py-4 px-6">Referencia</th>
              <th class="py-4 px-6 text-right">Monto USD</th>
              <th class="py-4 px-6 text-right">Monto alternativo</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="pay in payments" :key="pay.id" class="hover:bg-gray-50 transition-colors">
              <td class="py-4 px-6 text-gray-600">{{ formatDate(pay.created_at) }}</td>
              <td class="py-4 px-6 font-semibold text-teal-800">
                <Link :href="`/orders/${pay.order_id}`">Ver Venta</Link>
              </td>
              <td class="py-4 px-6 font-medium capitalize">{{ pay.method }}</td>
              <td class="py-4 px-6 text-gray-500 font-mono">{{ pay.reference || '-' }}</td>
              <td class="py-4 px-6 text-right font-bold text-gray-900">${{ formatPrice(pay.amount) }}</td>
              <td class="py-4 px-6 text-right font-medium text-gray-500">
                <span v-if="pay.amount_bs > 0">Bs. {{ formatPrice(pay.amount_bs) }}</span>
                <span v-else>-</span>
              </td>
            </tr>
            <tr v-if="payments.length === 0">
              <td colspan="6" class="text-center py-8 text-gray-500">
                No se registraron cobros en esta caja.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tab: Retiros / Movimientos -->
      <div v-if="activeTab === 'movements'" class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm">
          <thead>
            <tr class="text-left bg-gray-50 text-gray-500 text-xs font-semibold uppercase">
              <th class="py-4 px-6">Fecha/Hora</th>
              <th class="py-4 px-6 text-center">Tipo</th>
              <th class="py-4 px-6">Concepto / Descripción</th>
              <th class="py-4 px-6 text-right">Monto USD</th>
              <th class="py-4 px-6 text-right">Monto Bs</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="mov in movements" :key="mov.id" class="hover:bg-gray-50 transition-colors">
              <td class="py-4 px-6 text-gray-600">{{ formatDate(mov.created_at) }}</td>
              <td class="py-4 px-6 text-center">
                <span 
                  :class="[
                    'px-2.5 py-1 text-xs font-bold rounded-full uppercase tracking-wider inline-flex items-center gap-1',
                    mov.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  ]"
                >
                  <i :class="['fa text-xs', mov.type === 'income' ? 'fa-arrow-down' : 'fa-arrow-up']"></i>
                  <span>{{ mov.type === 'income' ? 'Ingreso' : 'Egreso' }}</span>
                </span>
              </td>
              <td class="py-4 px-6 text-gray-800 font-medium">{{ mov.description }}</td>
              <td 
                :class="[
                  'py-4 px-6 text-right font-bold',
                  mov.type === 'income' ? 'text-green-700' : 'text-red-600'
                ]"
              >
                {{ mov.amount > 0 ? `${mov.type === 'income' ? '+' : '-'}$${formatPrice(mov.amount)}` : '-' }}
              </td>
              <td 
                :class="[
                  'py-4 px-6 text-right font-bold',
                  mov.type === 'income' ? 'text-green-700' : 'text-red-600'
                ]"
              >
                {{ mov.amount_bs > 0 ? `${mov.type === 'income' ? '+' : '-'}Bs ${formatPrice(mov.amount_bs)}` : '-' }}
              </td>
            </tr>
            <tr v-if="movements.length === 0">
              <td colspan="5" class="text-center py-8 text-gray-500">
                No se registraron movimientos manuales (ingresos ni retiros).
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'

export default {
  name: 'CajasShow',

  components: {
    Head,
    Link,
    TextInput
  },

  layout: Layout,

  props: {
    caja: Object,
    orders: Array,
    payments: Array,
    movements: Array,
    change_by_method: Array,
    stats: Object,
    role: String
  },

  data() {
    return {
      activeTab: 'orders',
      movementForm: this.$inertia.form({
        type: 'income',
        amount: '',
        amount_bs: '',
        description: ''
      }),
      closeForm: this.$inertia.form({
        closing_amount: '',
        closing_amount_bs: '',
        notes: ''
      })
    }
  },

  computed: {
    discrepancy() {
      if (this.caja.closing_amount === null) return 0
      return this.caja.closing_amount - this.stats.expected_cash
    },
    discrepancy_bs() {
      if (this.caja.closing_amount_bs === null) return 0
      return this.caja.closing_amount_bs - this.stats.expected_cash_bs
    },
    tabs() {
      return [
        { id: 'orders', name: 'Ventas de la Caja', count: this.orders.length },
        { id: 'payments', name: 'Cobros Recibidos', count: this.payments.length },
        { id: 'movements', name: 'Movimientos (Ingresos / Egresos)', count: this.movements.length }
      ]
    }
  },

  methods: {
    back() {
      window.history.back()
    },
    formatPrice(value) {
      return Number(value).toFixed(2)
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    getOrderStateText(state) {
      const states = {
        cancel: 'ANULADA',
        request: 'PENDIENTE',
        finish: 'FINALIZADA', 
        pay: 'PAGADA',
        delivery: 'POR DESPACHAR'
      }
      return states[state] || state
    },
    getOrderStateClass(state) {
      const classes = {
        cancel: 'bg-red-100 text-red-800',
        request: 'bg-yellow-100 text-yellow-800',
        finish: 'bg-green-100 text-green-800', 
        pay: 'bg-blue-100 text-blue-800',
        delivery: 'bg-orange-100 text-orange-800'
      }
      return classes[state] || 'bg-gray-100 text-gray-800'
    },
    submitMovement() {
      if ((!this.movementForm.amount || parseFloat(this.movementForm.amount) <= 0) &&
          (!this.movementForm.amount_bs || parseFloat(this.movementForm.amount_bs) <= 0)) {
        this.$toast.error('Debe ingresar un monto mayor a 0 en USD o en Bs.')
        return
      }

      this.movementForm.post(`/cajas/${this.caja.id}/movements`, {
        onSuccess: () => {
          const typeLabel = this.movementForm.type === 'income' ? 'Ingreso' : 'Retiro'
          this.movementForm.reset('amount', 'amount_bs', 'description')
          this.$toast.success(`${typeLabel} de caja registrado correctamente.`)
        },
        onError: (err) => {
          this.$toast.error(err.amount || err.error || 'Ocurrió un error al registrar el movimiento.')
        }
      })
    },
    submitWithdrawal() {
      this.submitMovement()
    },
    submitCloseCaja() {
      if (confirm('¿Está seguro de cerrar esta caja? Esta acción realizará el arqueo y bloqueará nuevas operaciones.')) {
        this.closeForm.post(`/cajas/${this.caja.id}/close`, {
          onSuccess: () => {
            this.$toast.success('Caja cerrada con éxito.')
          },
          onError: (err) => {
            this.$toast.error(err.error || 'Ocurrió un error al cerrar la caja.')
          }
        })
      }
    },
    getUserName(user) {
      if (!user) return '-'
      if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }
      return user.name || '-'
    }
  }
}
</script>
